import type { ComponentPublicInstance } from 'vue';
import { VueRenderer } from '@tiptap/vue-3';
import type {
    SuggestionKeyDownProps,
    SuggestionOptions,
    SuggestionProps,
} from '@tiptap/suggestion';
import tippy, { type Instance as TippyInstance } from 'tippy.js';
import MentionDropdown from '@/components/container/lead/notes/MentionDropdown.vue';
import { filterMentionableUsers, type MentionableUser } from '@/hooks/mention/mentionableUsers.ts';

type MentionSelection = Pick<MentionableUser, 'id' | 'fullName'>;
type DropdownInstance = ComponentPublicInstance & {
    $el: Element;
};

const safeMentionName = (name: string) => name.replaceAll(/[[\]()]/g, '');

export const buildNoteMentionSuggestion = (
    users: () => ReadonlyArray<MentionableUser>,
    onMention: (userId: number | string) => void
): Omit<SuggestionOptions<MentionableUser, MentionSelection>, 'editor'> => ({
    char: '@',
    allowedPrefixes: [' ', '\n'],
    items: ({ query }) => [...filterMentionableUsers(users(), query)],
    command: ({ editor, range, props }) => {
        const label = safeMentionName(props.fullName);

        editor
            .chain()
            .focus()
            .insertContentAt(range, [
                {
                    type: 'mention',
                    attrs: {
                        id: String(props.id),
                        label,
                    },
                },
                { type: 'text', text: ' ' },
            ])
            .run();
        onMention(props.id);
    },
    render: () => {
        let component: VueRenderer<DropdownInstance> | null = null;
        let popup: TippyInstance | null = null;
        let activeIndex = 0;
        let currentProps: SuggestionProps<MentionableUser, MentionSelection> | null = null;

        const updateComponent = () => {
            if (!component || !currentProps) {
                return;
            }

            component.updateProps({
                items: currentProps.items,
                activeIndex,
                positioned: false,
                onSelect: (item: MentionableUser) => currentProps?.command(item),
                onHover: (index: number) => {
                    activeIndex = index;
                    updateComponent();
                },
            });
        };

        const selectActiveItem = () => {
            const item = currentProps?.items[activeIndex];

            if (!item) {
                return false;
            }

            currentProps?.command(item);
            return true;
        };

        const getReferenceClientRect = () =>
            currentProps?.clientRect?.() ?? new DOMRect(0, 0, 0, 0);

        const dropdownProps = () => ({
            items: currentProps?.items ?? [],
            activeIndex,
            positioned: false,
            onSelect: (item: MentionableUser) => currentProps?.command(item),
            onHover: (index: number) => {
                activeIndex = index;
                updateComponent();
            },
        });

        return {
            onStart: (props) => {
                currentProps = props;
                activeIndex = 0;
                component = new VueRenderer(MentionDropdown, {
                    editor: props.editor,
                    props: dropdownProps(),
                });

                popup = tippy(document.body, {
                    appendTo: () => document.body,
                    arrow: false,
                    content: component.element,
                    getReferenceClientRect,
                    interactive: true,
                    placement: 'bottom-start',
                    showOnCreate: true,
                    theme: 'note-mention',
                    trigger: 'manual',
                });
            },
            onUpdate: (props) => {
                currentProps = props;
                activeIndex = 0;
                updateComponent();
                popup?.setProps({ getReferenceClientRect });
            },
            onKeyDown: ({ event }: SuggestionKeyDownProps) => {
                const consumeEvent = () => {
                    event.preventDefault();
                    event.stopPropagation();
                };

                if (event.key === 'Escape') {
                    consumeEvent();
                    popup?.hide();
                    return true;
                }

                if (!currentProps?.items.length) {
                    return false;
                }

                if (event.key === 'ArrowDown') {
                    consumeEvent();
                    activeIndex = (activeIndex + 1) % currentProps.items.length;
                    updateComponent();
                    return true;
                }

                if (event.key === 'ArrowUp') {
                    consumeEvent();
                    activeIndex =
                        (activeIndex - 1 + currentProps.items.length) % currentProps.items.length;
                    updateComponent();
                    return true;
                }

                if (event.key === 'Enter' || event.key === 'Tab') {
                    consumeEvent();
                    return selectActiveItem();
                }

                return false;
            },
            onExit: () => {
                popup?.destroy();
                component?.destroy();
                popup = null;
                component = null;
                currentProps = null;
            },
        };
    },
});
